import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { ApiClientDto } from "@/features/admin/api/dto";
import { useRevokeApiClient } from "@/features/admin/api/use-revoke-api-client";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";

/**
 * Confirmation de révocation d'un client API (`POST /ApiClients/{id}/revoke`,
 * §6.1) — action irréversible, preview ShareablePreview avant l'appel.
 */
export function ApiClientRevokeDialog({
  apiClient,
  open,
  onOpenChange,
}: {
  apiClient: ApiClientDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useAdminTranslation();
  const revoke = useRevokeApiClient();
  const pending = revoke.isPending;

  if (!open) return null;

  return (
    <ShareablePreview
      data={{
        id: apiClient.id,
        name: apiClient.name,
        status: apiClient.status,
        scopes: apiClient.scopes,
      }}
      title={`${t("admin.apiClients.revoke.title")} — ${apiClient.name}`}
      description={t("admin.apiClients.revoke.description")}
      mode="user"
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen && !pending) onOpenChange(false);
      }}
      maxHeight="90vh"
      customView={() => (
        <p className="text-sm text-muted-foreground">
          <NoTranslate as="span">{apiClient.name}</NoTranslate>
        </p>
      )}
      actions={createPreviewActions({
        onConfirm: () =>
          revoke.mutate(apiClient.id, {
            onSuccess: () => {
              notifSilence(t("admin.apiClients.feedback.revoked"));
              onOpenChange(false);
            },
            onError: (error) => notifSilenceError(error.message),
          }),
        onCancel: () => onOpenChange(false),
        t,
        labels: { confirm: t("admin.apiClients.actions.revoke") },
        confirmLoading: pending,
        withIndicatorOnConfirm: true,
      })}
    />
  );
}
